import { TriggerStep } from "app/lib/pipeline";
import BaseNode from "./BaseNode";
import { Node, NodeProps } from "@xyflow/react";
import * as HoverCard from "./StepHoverCard";
import { useMostRecentJob, useBuild } from "app/components/build/Show/lib/BuildContext";
import { shortCommit } from "app/lib/commits";
import { TriggerJob } from "app/components/build/Show/lib/types";
import Button from "app/components/shared/Button";
import Emojify from "app/components/shared/Emojify";
import { JobDuration } from "app/components/job/Job/JobDuration";
import { StateIcon, StateLabel } from "./BaseNode/StateIcon";
import { textColorForStep } from "./colors";
import { useCallback } from "react";
import { MissingStepDependencies } from "./MissingStepDependencies";

const TriggerHover = (
  props: NodeProps<TriggerStepNodeType> & {
    label: string;
  },
) => {
  const job = useMostRecentJob<TriggerJob>(props.id);
  const { build } = useBuild({ strict: false });
  if (!build || !job || !props.data.state) {
    return null;
  }

  const commit = shortCommit(job.triggerCommit, job.triggerCommitShortLength);
  const pipeline = job?.triggerProject?.name || props.data.triggerPipelineSlug;

  // Don't select the node when clicking on the build link
  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  return (
    <div className="space-y-3">
      <div>
        <div className="flex gap-2 items-baseline">
          <div className="flex-auto min-w-0">
            <Emojify text={props.label} className="font-medium break-words" />
          </div>
          <span className="flex items-center gap-2 ml-auto">
            <StateLabel {...props.data} />
            <StateIcon className={textColorForStep(props.data)} {...props.data} />
          </span>
        </div>
        <div className="flex gap-2">
          {job.startedAt && (
            <span className="whitespace-nowrap">Build #{job.triggeredBuild?.number}</span>
          )}

          <span className="text-charcoal-300">
            {job.triggerAsync ? "(asynchronous)" : <JobDuration job={job} build={build} />}
          </span>
        </div>
      </div>

      <div>
        <span>
          <code className="monospace">{job.triggerBranch}</code>
          {" \x2F "}
          <code className="monospace" title={job.triggerCommit}>
            {commit}
          </code>
        </span>
        {" on "}
        <span className="font-medium">{pipeline}</span>
      </div>

      <MissingStepDependencies missingDependencies={job.missingDependencies} build={build} />

      {job.triggeredBuild && (
        <div>
          <Button
            href={job.triggeredBuild?.canvasUrl}
            theme="small"
            onClick={handleClick}
            target="_blank"
          >
            <span className="after:content-['→'] after:ml-1">Go to build</span>
          </Button>
        </div>
      )}
    </div>
  );
};

type TriggerStepNodeType = Node<TriggerStep, "trigger">;

export default function TriggerStepNode(props: NodeProps<TriggerStepNodeType>) {
  const job = useMostRecentJob<TriggerJob>(props.id);
  const label =
    props.data.label ||
    `Trigger build on ${job?.triggerProject?.name || props.data.triggerPipelineSlug}`;

  return (
    <HoverCard.Root>
      <HoverCard.Portal container={document.getElementById("canvas")}>
        {props.data.state && (
          <HoverCard.Content
            state={props.data.state}
            outcome={props.data.outcome}
            missingDependencies={props.data.missingDependencies}
            collisionBoundary={document.getElementById("canvas")}
            collisionPadding={10}
          >
            {job && <TriggerHover {...props} label={label} />}
          </HoverCard.Content>
        )}
      </HoverCard.Portal>

      <HoverCard.Trigger asChild={true}>
        <div>
          <BaseNode {...props} label={<Emojify text={label} />} />
        </div>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}
